import { useState } from "react";
import PropTypes from "prop-types";
import { Field, Formik } from "formik";
import { Col, Row } from "react-grid-system";
import * as Yup from "yup";
import { Button } from "../Button";
import { StyledField } from "../../styles/StyledField";
import { SForm, SFormFooter } from "../../styles/ChangePassword";
import AddIcon from "../../assets/img/icons/add.svg";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { addOrganizationAdmin, createOrganization } from "../../api/superadmin/organizations";

const adminSchema = {
	firstName: Yup.string().required("First name is required"),
	lastName: Yup.string().required("Last name is required"),
	email: Yup.string().email("Must be a valid email").required("Email is required"),
	password: Yup.string()
		.required("Password is required")
		.min(8, "At least 8 characters")
		.matches(/[a-z]/, "At least one lowercase letter")
		.matches(/[A-Z]/, "At least one uppercase letter")
		.matches(/[0-9]/, "At least one number"),
};

const FIELDS = [
	["firstName", "First name", "text"],
	["lastName", "Last name", "text"],
	["email", "Email", "email"],
	["password", "Password", "password"],
];

/**
 * Creates an organization with its first admin or, with an organization, adds an admin to it.
 * The admin logs in with the email and password set here.
 */
export default function OrganizationForm({ organization, onSaved, onCancel }) {
	const { axiosPrivate } = useAxiosPrivate();
	const [apiErrors, setApiErrors] = useState([]);
	const newOrganization = !organization;

	// API paths look like "data.name" or "children[admin].children[email].data"
	const apiErrorsFor = (field) =>
		apiErrors.filter((error) => error.path?.endsWith(`.${field}`) || error.path?.includes(`[${field}]`)).map((error) => error.message);

	const submit = async ({ name, ...admin }) => {
		setApiErrors([]);
		const res = newOrganization
			? await createOrganization(axiosPrivate, { name, admin })
			: await addOrganizationAdmin(axiosPrivate, organization.id, admin);
		if (res?.status === 201) {
			onSaved();
		} else if (res?.status === 400) {
			setApiErrors(res.data?.fieldErrors ?? []);
		}
	};

	const fieldRow = (name, label, type, errors, touched) => (
		<StyledField>
			<label htmlFor={`organization-${name}`}>{label}</label>
			<Field id={`organization-${name}`} name={name} type={type} autoComplete={type === "password" ? "new-password" : "off"} />
			{errors[name] && touched[name] && <span>{errors[name]}</span>}
			{apiErrorsFor(name).map((message) => (
				<span key={message}>{message}</span>
			))}
		</StyledField>
	);

	return (
		<>
			<h2>{newOrganization ? "Create organization" : `Add organization admin to ${organization.name}`}</h2>
			<Formik
				initialValues={{ name: "", firstName: "", lastName: "", email: "", password: "" }}
				validationSchema={Yup.object(newOrganization ? { name: Yup.string().required("Name is required"), ...adminSchema } : adminSchema)}
				onSubmit={submit}
			>
				{({ errors, touched, isSubmitting }) => (
					<SForm>
						{newOrganization && (
							<>
								{fieldRow("name", "Name", "text", errors, touched)}
								<h3 style={{ margin: "2rem 0 0" }}>Organization admin</h3>
								<p style={{ margin: "0.4rem 0 1.2rem" }}>
									Manages the organization&apos;s users and units. Logs in with this email and password.
								</p>
							</>
						)}
						<Row>
							{FIELDS.map(([name, label, type]) => (
								<Col sm={12} md={6} key={name}>
									{fieldRow(name, label, type, errors, touched)}
								</Col>
							))}
						</Row>
						<SFormFooter>
							<Button type="reset" onClick={onCancel}>
								Cancel
							</Button>
							<Button type="submit" disabled={isSubmitting} icon={AddIcon}>
								{newOrganization ? "Create" : "Add admin"}
							</Button>
						</SFormFooter>
					</SForm>
				)}
			</Formik>
		</>
	);
}

OrganizationForm.propTypes = {
	organization: PropTypes.shape({ id: PropTypes.number.isRequired, name: PropTypes.string }),
	onSaved: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired,
};
